import { ArrowLeft, Calendar } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";

const GutRestDetail = () => {
  const navigate = useNavigate();

  const dailyData = Array.from({ length: 7 }, (_, i) => ({
    time: `${i * 2}h`,
    hours: Math.floor(Math.random() * 4 + 12),
  }));

  const weeklyData = [
    { day: "Mon", hours: 14.5 },
    { day: "Tue", hours: 13.2 },
    { day: "Wed", hours: 15.8 },
    { day: "Thu", hours: 14.0 },
    { day: "Fri", hours: 13.5 },
    { day: "Sat", hours: 16.2 },
    { day: "Sun", hours: 15.0 },
  ];

  const monthlyData = [
    { week: "Week 1", hours: 14.5 },
    { week: "Week 2", hours: 13.8 },
    { week: "Week 3", hours: 15.2 },
    { week: "Week 4", hours: 14.8 },
  ];

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera" + GluceraNavigationRoutes.HOME)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">Gut Rest (Agni)</h1>
          <Button variant="ghost" size="icon" className="rounded-xl">
            <Calendar className="h-5 w-5" />
          </Button>
        </div>

        {/* Today's Stats */}
        <Card className="rounded-2xl border-0 bg-gradient-to-br from-chart-2/10 to-chart-2/5 p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Fasting Window
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">15.2</div>
            <p className="text-sm text-muted-foreground">hours (Excellent)</p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="text-xs text-muted-foreground">Short</span>
              <div className="via-warning to-success h-2 w-48 rounded-full bg-gradient-to-r from-destructive">
                <div className="relative h-full w-full">
                  <div className="absolute left-[85%] top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-background bg-foreground" />
                </div>
              </div>
              <span className="text-xs text-muted-foreground">Optimal</span>
            </div>
          </div>
        </Card>

        {/* What is Gut Rest */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Gut Rest (Agni)?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Gut rest, or digestive fire (Agni in Ayurveda), measures the time
            between your last meal and first meal of the day. Adequate fasting
            periods allow your digestive system to rest, repair, and optimize
            metabolic processes. This practice is associated with improved gut
            health, better insulin sensitivity, and cellular repair through
            autophagy.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Optimal Rest</span>
              <span className="text-success font-semibold">14-16+ hours</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good Rest</span>
              <span className="font-semibold text-primary">12-14 hours</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Moderate Rest</span>
              <span className="text-warning font-semibold">10-12 hours</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Insufficient Rest</span>
              <span className="font-semibold text-destructive">
                &lt;10 hours
              </span>
            </div>
          </div>
        </Card>

        {/* Charts */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <Tabs defaultValue="daily" className="w-full">
            <TabsList className="mb-4 grid w-full grid-cols-3">
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="daily" className="space-y-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="time"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "12px" }}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "12px" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="hours"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--primary))", r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="weekly" className="space-y-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="day"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "12px" }}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "12px" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="hours"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--primary))", r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="monthly" className="space-y-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                    />
                    <XAxis
                      dataKey="week"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "12px" }}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "12px" }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="hours"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--primary))", r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Key Insights */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">Key Insights</h3>
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="bg-success mt-2 h-2 w-2 rounded-full" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  Your weekend fasting windows are consistently longer and more
                  beneficial
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  You've maintained at least 14 hours of fasting for 5 days this
                  week
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="bg-warning mt-2 h-2 w-2 rounded-full" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">
                  Consider having dinner 1 hour earlier to extend your fasting
                  window
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default GutRestDetail;
